import { useCallback } from "react";
import { Button } from "../../ui/button";

export interface SegmentedPipsProps {
  value: number;
  max: number;
  onChange: (next: number) => void;
  pipSize?: number;
  gap?: number;
  filledColor?: string;
  emptyColor?: string;
  disabled?: boolean;
  label?: string;
  ariaLabel?: string;
}

/**
 * Charges as squares: a tap on the last filled one spends it, on an empty one fills up to it.
 * GN (re-pass Н6 / №46): each square is a real button (role, focus, Enter) with a 24 px hit area around the 14 px mark —
 * it was a bare 14 px <div> without a role.
 */
export function SegmentedPips({
  value,
  max,
  onChange,
  pipSize = 14,
  gap = 4,
  filledColor = "var(--paper-gold)",
  emptyColor = "var(--paper-rule)",
  disabled = false,
  label,
  ariaLabel,
}: SegmentedPipsProps) {
  const handlePipClick = useCallback(
    (i: number) => {
      if (i + 1 === value) {
        onChange(value - 1);
      } else {
        onChange(i + 1);
      }
    },
    [value, onChange],
  );

  const hit = Math.max(24, pipSize);
  return (
    <div style={disabled ? { opacity: 0.5, pointerEvents: "none", cursor: "not-allowed" } : undefined}>
      {label && <div className="mb-1 text-label font-semibold uppercase tracking-label text-dim">{label}</div>}
      <div role="group" aria-label={ariaLabel} className="flex flex-row" style={{ gap: Math.max(0, gap - (hit - pipSize)) }}>
        {Array.from({ length: max }, (_, i) => (
          <Button
            key={i}
            type="button"
            variant="ghost"
            size="inline"
            disabled={disabled}
            aria-pressed={i < value}
            aria-label={`${ariaLabel ?? ""} ${i + 1}/${max}`.trim()}
            onClick={() => handlePipClick(i)}
            className="shrink-0 rounded-none p-0 hover:bg-transparent"
            style={{ width: hit, height: hit }}
          >
            <span aria-hidden="true" style={{ width: pipSize, height: pipSize, background: i < value ? filledColor : emptyColor, display: "block" }} />
          </Button>
        ))}
      </div>
    </div>
  );
}
